import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../api/apiClient';
import { useClient } from '../context/ClientContext';

const EMPTY_FEEDBACK = {
  posture: '',
  recommendations: '',
  clientSummary: '',
};

function normalizeFeedback(feedback) {
  return {
    posture: feedback?.posture || '',
    recommendations: feedback?.recommendations || '',
    clientSummary: feedback?.clientSummary || '',
  };
}

function feedbackPath(clientId, pageKey, entityKey) {
  const query = entityKey ? `?entityKey=${encodeURIComponent(entityKey)}` : '';
  return `/clients/${clientId}/page-feedback/${encodeURIComponent(pageKey)}${query}`;
}

export function usePageFeedback(meta) {
  const { clientId } = useClient();
  const api = useApiClient();
  const pageKey = meta?.pageKey || null;
  const entityKey = meta?.entityKey || null;
  const enabled = Boolean(clientId && pageKey);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const path = useMemo(
    () => enabled ? feedbackPath(clientId, pageKey, entityKey) : null,
    [clientId, enabled, entityKey, pageKey],
  );

  const load = useCallback(async () => {
    if (!path) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await api.get(path);
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [api, path]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async (feedback) => {
    if (!path || !pageKey) return null;

    setSaving(true);
    setSaveError(null);
    try {
      const result = await api.put(path, {
        ...normalizeFeedback(feedback),
        entityKey,
      });
      setData(result);
      return result;
    } catch (err) {
      setSaveError(err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [api, entityKey, pageKey, path]);

  return {
    data,
    feedback: normalizeFeedback(data?.feedback ?? EMPTY_FEEDBACK),
    hasContent: Boolean(data?.hasContent),
    canEdit: Boolean(data?.canEdit),
    resetFromPreviousScan: Boolean(data?.resetFromPreviousScan),
    sourceScanJobId: data?.sourceScanJobId ?? null,
    sourceScanCompletedAt: data?.sourceScanCompletedAt ?? null,
    updatedAt: data?.updatedAt ?? null,
    updatedBy: data?.updatedBy ?? null,
    loading,
    error,
    saving,
    saveError,
    save,
    reload: load,
  };
}
